import {
  mergeLegacyFieldIds,
  type FieldIdRef,
  unwrapCatalogFieldsResponse,
} from './resolveFieldId.ts'

type HttpGet = { get: (url: string) => Promise<unknown> }

/** Кэш ответа GET /object-types/:id/fields на время сессии — пресет может
 *  содержать несколько условий одного типа. */
const catalogFieldsCache = new Map<number, ReturnType<typeof unwrapCatalogFieldsResponse>>()

/**
 * Нужен ли доп. запрос каталога. После inventory 1.3 `/object-types/full`
 * кладёт `legacy_field_id` в fields. Если у любого поля legacy уже есть —
 * считаем каталог нормализованным и HTTP не делаем. Запрос остаётся fallback
 * только когда full отдал поля совсем без legacy (старый JSONB типа).
 */
export const fieldsNeedLegacyEnrichment = (fields: FieldIdRef[]): boolean => {
  if (!fields.length) return false
  return !fields.some((field) => Boolean(field.legacy_field_id))
}

/**
 * Подмешивает legacy_field_id в поля типа, если их ещё нет в ответе full.
 * Нужно, чтобы пресеты со старыми 8-символьными field_id находили поле.
 *
 * @param http — клиент из `useHttp()` (вызывать только из setup/компонента)
 */
export const enrichFieldsWithLegacyIds = async (
  objectTypeId: number,
  fields: FieldIdRef[],
  http: HttpGet,
): Promise<void> => {
  if (!objectTypeId || !fields.length) return
  if (!fieldsNeedLegacyEnrichment(fields)) return

  let rows = catalogFieldsCache.get(objectTypeId)
  if (!rows) {
    try {
      const res = await http.get(`/object-types/${objectTypeId}/fields`)
      rows = unwrapCatalogFieldsResponse(res)
      catalogFieldsCache.set(objectTypeId, rows)
    } catch {
      // Без каталога пресеты с уже числовыми id продолжают работать;
      // legacy просто не резолвится.
      return
    }
  }

  mergeLegacyFieldIds(fields, rows)
}

/** Сброс кэша — для тестов. */
export const clearCatalogFieldsCache = () => catalogFieldsCache.clear()
